import { useMutation } from '@apollo/client';
import { getOperationName } from '@apollo/client/utilities';
import { transformQuoteFormApiErrors } from '@shared/Quotes/Form/transformers';
import { QuoteFormWihoutExtraFileFieldValues } from '@shared/Quotes/Form/types';
import { ITP_RFQ_CONTEXT_QUERY } from '../../../context/queries';
import { CREATE_OR_REVISE_QUOTE_FOR_RFQ } from './mutations';
import { CreateOrReviseQuoteForRfqInput } from '../../../types/global';
import {
  ItpCreateOrReviseQuoteForRfq,
  ItpCreateOrReviseQuoteForRfqVariables,
} from './types/ItpCreateOrReviseQuoteForRfq';
import { EntityId } from '@ascension/types';

export const useCreateOrReviseQuote = (rfqId: EntityId, openSignupModal: boolean) => {
  const reviseQuoteOperationName = getOperationName(ITP_RFQ_CONTEXT_QUERY);

  if (!reviseQuoteOperationName) {
    // TODO: make the mutation update the cache instead
    throw Error('Could not get operation name for ITP_RFQ_CONTEXT_QUERY');
  }

  const [createOrReviseQuote] = useMutation<
    ItpCreateOrReviseQuoteForRfq,
    ItpCreateOrReviseQuoteForRfqVariables
  >(CREATE_OR_REVISE_QUOTE_FOR_RFQ, {
    refetchQueries: [reviseQuoteOperationName],
    onCompleted: (data) => {
      if (data.createOrReviseQuoteForRfq.__typename === 'LimitedQuote') {
        if (openSignupModal) {
          // After mutation is successful, we will reload the page from the global js.
          // See assets/src/js/itp/cta.js
          const openSignUpModalEvent = new CustomEvent('openSignUpModal');
          document.dispatchEvent(openSignUpModalEvent);
          return data.createOrReviseQuoteForRfq.id;
        }
        location.reload();
      }
      return undefined;
    },
  });

  const submit = async (values: QuoteFormWihoutExtraFileFieldValues) => {
    const { quoteAmount, quoteDetails, quoteFile } = values;

    if (!quoteAmount || !quoteFile) {
      return undefined;
    }

    const transformedInput: CreateOrReviseQuoteForRfqInput = {
      rfqId,
      amount: quoteAmount,
      details: quoteDetails,
      quoteFile: {
        hash: quoteFile.hash,
        fileSize: quoteFile.size,
        fileName: quoteFile.name,
        contentType: quoteFile.type,
      },
    };

    const { data: response } = await createOrReviseQuote({
      variables: { input: transformedInput },
    });

    if (response?.createOrReviseQuoteForRfq.__typename === 'Errors') {
      return transformQuoteFormApiErrors(response.createOrReviseQuoteForRfq.errors);
    }

    return undefined;
  };

  return { submit };
};
